import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosClient from '../clients/axiosClient';
import type { OnboardingStatus, OnboardingStepKey } from '../onboarding/onboardingSteps';
import { applyLocalStepVisit } from '../onboarding/onboardingNavigation';
import { onboardingKeys } from './queryKeys';

export function useOnboardingStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: onboardingKeys.status(),
    queryFn: async () => {
      const { data } = await axiosClient.get<OnboardingStatus>('/api/onboarding/status');
      return data;
    },
    enabled: options?.enabled !== false,
  });
}

export function useCompleteStepMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stepKey: string) =>
      axiosClient.post<OnboardingStatus>(`/api/onboarding/steps/${stepKey}/complete`),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(onboardingKeys.status(), data);
    },
  });
}

export function useVisitStepMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stepKey: OnboardingStepKey): Promise<{ status: OnboardingStatus; synced: boolean }> => {
      try {
        const { data } = await axiosClient.post<OnboardingStatus>(
          `/api/onboarding/steps/${stepKey}/visit`,
        );
        return { status: data, synced: true };
      } catch (err: unknown) {
        const current = queryClient.getQueryData<OnboardingStatus>(onboardingKeys.status());
        if (current) {
          const next = applyLocalStepVisit(current, stepKey);
          if (next) return { status: next, synced: false };
        }
        const apiError = axios.isAxiosError(err)
          ? err.response?.data?.error || err.message
          : err instanceof Error
            ? err.message
            : undefined;
        throw new Error(apiError || 'Could not go to that step');
      }
    },
    onSuccess: ({ status }) => {
      queryClient.setQueryData(onboardingKeys.status(), status);
    },
  });
}
